import { api } from './api';

/**
 * The confidentiality notice every user confirms once before using the system (Zero.Auth
 * `LegalNotice`). Its TEXT is `legalNotice.*` in the locale files; this is the version of that
 * text. Rewording the notice means bumping this AND `LegalNotice.CurrentVersion` in Zero.Auth in
 * the same change, which asks every user to confirm again — an acceptance records one exact text.
 */
export const LEGAL_NOTICE_VERSION = '2026-10-12';

/** `legalNotice` on `GET /auth/profile`. Absent from a Zero.Auth older than the notice. */
export interface LegalNoticeStatus {
  version: string;
  required: boolean;
  acceptedAt: string | null;
}

/** The `code` of the gateway's 403 for an account that has not confirmed yet. */
export const LEGAL_NOTICE_REFUSAL_CODE = 'legal_notice_required';

/**
 * Whether the profile says this account still owes the notice. Only an explicit `required: true`
 * counts, so a backend that predates the notice never blocks anyone.
 */
export function owesLegalNotice(profile: { legalNotice?: LegalNoticeStatus | null } | undefined): boolean {
  return profile?.legalNotice?.required === true;
}

/** The gateway refused a request because the account has not confirmed the notice. */
export function isLegalNoticeRefusal(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return response?.status === 403 && response.data?.code === LEGAL_NOTICE_REFUSAL_CODE;
}

/**
 * Record the confirmation. Sends the version this build SHOWED and the language it was shown in;
 * Zero.Auth answers 409 when that is not the current version, so a stale page can never record an
 * acceptance of a text the user did not see.
 */
export async function acceptLegalNotice(language: string): Promise<{ version: string; acceptedAt: string }> {
  const { data } = await api.post('/auth/legal-notice/accept', {
    version: LEGAL_NOTICE_VERSION,
    language,
  });
  return data;
}
